/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   slaService.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   API service and React Query hooks for SLA blast radius
 *     Constitution    :   v1.12.0 | Enterprise Dependency Architecture
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../apiClient';

// --- Query Keys ---
export const OPERATIONS_SLA_KEYS = {
  all: ['operations', 'sla'] as const,
  status: () => [...OPERATIONS_SLA_KEYS.all, 'status'] as const,
  dependencies: () => [...OPERATIONS_SLA_KEYS.all, 'dependencies'] as const,
  blastRadius: (capacityId: string) => [...OPERATIONS_SLA_KEYS.all, 'blast-radius', capacityId] as const,
  alerts: () => [...OPERATIONS_SLA_KEYS.all, 'alerts'] as const,
  datasources: () => [...OPERATIONS_SLA_KEYS.all, 'datasources'] as const,
  datasourceBlast: (key: string) => [...OPERATIONS_SLA_KEYS.all, 'datasource-blast', key] as const,
  assets: () => [...OPERATIONS_SLA_KEYS.all, 'assets'] as const,
  assetBlast: (key: string) => [...OPERATIONS_SLA_KEYS.all, 'asset-blast', key] as const,
};

// --- Types ---
export interface SlaStatusRow {
  capacity_type: string;
  total_capacities: number;
  healthy_count: number;
  warning_count: number;
  critical_count: number;
  compliance_percentage: number;
}

export interface DependencyNode {
  id: string;
  capacity_name: string;
  capacity_type: string;
  status: string;
  workspace_count: number;
}

export interface DependencyEdge {
  id: string;
  source: string;            // dependent capacity (depends on target)
  target: string;            // dependency capacity
  dependency_type: string;
  impact_severity: string;
  source_name: string;
  target_name: string;
}

export interface CreateDependencyInput {
  dependentCapacityId: string;
  dependencyCapacityId: string;
  dependencyType: string;
  impactSeverity: string;
}

export interface DependenciesResponse {
  nodes: DependencyNode[];
  edges: DependencyEdge[];
}

export interface BlastRadiusResult {
  affected_capacities: number;
  affected_workspaces: number;
  affected_clients: number;
  sla_breach_percentage: number;
  impacted_nodes: string[];
}

export interface SimulationResult extends BlastRadiusResult {
  source_capacity_id: string;
  cascade_path: string[];
}

export interface SlaAlertRow {
  id: string;
  capacity_name: string;
  capacity_type: string;
  severity: string;
  breach_description: string;
  occurred_at: string;
}

export interface DatasourceItem {
  datasource_type: string;
  server: string | null;
  database_name: string | null;
  model_count: number;
}

export interface DatasourceAffectedWorkspace {
  workspace_id: string;
  workspace_name: string;
  capacity_name: string | null;
  dataset_count: number;
}

export interface DatasourceBlastRadius {
  affected_datasets: number;
  affected_workspaces: number;
  affected_capacities: number;
  affected_clients: number;
  workspaces: DatasourceAffectedWorkspace[];
}

export interface LhWhAsset {
  id: string;
  name: string;
  kind: 'lakehouse' | 'warehouse';
  consumer_models: number;
}

// --- API Functions ---
async function fetchSlaStatus(): Promise<SlaStatusRow[]> {
  const { data } = await apiClient.get('/api/operations/sla/status');
  return data.data;
}

async function fetchDependencies(): Promise<DependenciesResponse> {
  const { data } = await apiClient.get('/api/operations/sla/dependencies');
  return data.data;
}

async function fetchDependencyGraph(): Promise<DependenciesResponse> {
  const { data } = await apiClient.get('/api/operations/sla/dependency-graph');
  return data.data;
}

async function fetchBlastRadius(capacityId: string): Promise<BlastRadiusResult> {
  const { data } = await apiClient.get('/api/operations/sla/blast-radius', { params: { capacityId } });
  return data.data;
}

async function simulateFailure(capacityId: string): Promise<SimulationResult> {
  const { data } = await apiClient.post('/api/operations/sla/simulate', { capacityId });
  return data.data;
}

async function fetchAlerts(): Promise<SlaAlertRow[]> {
  const { data } = await apiClient.get('/api/operations/sla/alerts');
  return data.data;
}

async function createDependency(input: CreateDependencyInput): Promise<DependencyEdge> {
  const { data } = await apiClient.post('/api/operations/sla/dependencies', input);
  return data.data;
}

async function deleteDependency(id: string): Promise<void> {
  await apiClient.delete(`/api/operations/sla/dependencies/${id}`);
}

async function fetchDatasources(): Promise<DatasourceItem[]> {
  const { data } = await apiClient.get('/api/operations/sla/datasources');
  return data.data;
}

async function fetchDatasourceBlastRadius(p: {
  datasourceType: string;
  server: string;
  database: string;
}): Promise<DatasourceBlastRadius> {
  const { data } = await apiClient.get('/api/operations/sla/datasource-blast-radius', {
    params: { datasourceType: p.datasourceType, server: p.server, database: p.database },
  });
  return data.data;
}

async function fetchAssets(): Promise<LhWhAsset[]> {
  const { data } = await apiClient.get('/api/operations/sla/assets');
  return data.data;
}

async function fetchAssetBlastRadius(p: { kind: string; id: string }): Promise<DatasourceBlastRadius> {
  const { data } = await apiClient.get('/api/operations/sla/asset-blast-radius', {
    params: { kind: p.kind, id: p.id },
  });
  return data.data;
}

// --- Hooks ---
export function useSlaStatus() {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.status(),
    queryFn: fetchSlaStatus,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useDependencies() {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.dependencies(),
    queryFn: fetchDependencies,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useDependencyNetwork() {
  return useQuery({
    queryKey: [...OPERATIONS_SLA_KEYS.all, 'dependency-graph'] as const,
    queryFn: fetchDependencyGraph,
    staleTime: 60_000,
  });
}

export function useBlastRadius(capacityId: string) {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.blastRadius(capacityId),
    queryFn: () => fetchBlastRadius(capacityId),
    enabled: !!capacityId,
    staleTime: 0,
  });
}

export function useSimulateFailure() {
  return useMutation({
    mutationFn: simulateFailure,
  });
}

export function useSlaAlerts() {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.alerts(),
    queryFn: fetchAlerts,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useCreateDependency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDependency,
    onSuccess: () => qc.invalidateQueries({ queryKey: OPERATIONS_SLA_KEYS.dependencies() }),
  });
}

export function useDeleteDependency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDependency,
    onSuccess: () => qc.invalidateQueries({ queryKey: OPERATIONS_SLA_KEYS.dependencies() }),
  });
}

export function useDatasources() {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.datasources(),
    queryFn: fetchDatasources,
    staleTime: 60_000,
  });
}

export function useDatasourceBlastRadius(
  p: { datasourceType: string; server: string; database: string } | null
) {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.datasourceBlast(
      p ? `${p.datasourceType}|${p.server}|${p.database}` : ''
    ),
    queryFn: () => fetchDatasourceBlastRadius(p!),
    enabled: !!p?.datasourceType,
    staleTime: 0,
  });
}

export function useLakehouseWarehouseAssets() {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.assets(),
    queryFn: fetchAssets,
    staleTime: 60_000,
  });
}

export function useAssetBlastRadius(p: { kind: string; id: string } | null) {
  return useQuery({
    queryKey: OPERATIONS_SLA_KEYS.assetBlast(p ? `${p.kind}|${p.id}` : ''),
    queryFn: () => fetchAssetBlastRadius(p!),
    enabled: !!p?.id,
    staleTime: 0,
  });
}
